import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import {
    AlertTriangle, ArrowRight, CreditCard, FileText, Receipt,
    Clock, DollarSign, RefreshCcw, MoreVertical,
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * "What Needs Attention Today" — the elevated Action Center strip.
 *
 * Snooze support (Iteration 42):
 *   - Snooze a bucket for 1 day / 7 days / 30 days.
 *   - "Dismiss until refresh" still available for one-off ignores.
 *   - Storage schema: { [key]: iso_string } in localStorage.
 *     A special ISO of "9999-12-31…" represents old permanent dismisses
 *     (migrated from the previous array-based schema on first read).
 */
const BUCKETS = [
    { key: "payments_due",       label: "Payments due (7d)",        icon: DollarSign,   path: "/payments",       cta: "Record",    tone: "amber" },
    { key: "overdue_cards",      label: "Overdue cards",            icon: AlertTriangle, path: "/payments",       cta: "Pay now",   tone: "rose" },
    { key: "missing_statements", label: "Missing statements",       icon: FileText,     path: "/statements",     cta: "Upload",    tone: "amber" },
    { key: "unreflected",        label: "Payments not reflected",   icon: RefreshCcw,   path: "/payments",       cta: "Reconcile", tone: "amber" },
    { key: "missing_receipts",   label: "Missing receipts",         icon: Receipt,      path: "/expenses",       cta: "Attach",    tone: "stone" },
    { key: "high_utilization",   label: "High utilisation cards",   icon: CreditCard,   path: "/payments",       cta: "Pay down",  tone: "rose" },
    { key: "renewals_soon",      label: "Subs renewing soon",       icon: Clock,        path: "/subscriptions",  cta: "Review",    tone: "stone" },
];

const TONE_MAP = {
    rose:  { text: "text-rose-700",  bg: "bg-rose-50",  border: "border-rose-200",  dot: "bg-rose-500" },
    amber: { text: "text-amber-800", bg: "bg-amber-50", border: "border-amber-200", dot: "bg-amber-500" },
    stone: { text: "text-stone-700", bg: "bg-stone-50", border: "border-stone-200", dot: "bg-stone-400" },
};

const LS_KEY = "vault.attn.snoozes";
const FAR_FUTURE = "9999-12-31T00:00:00Z";

function readSnoozes() {
    try {
        const raw = localStorage.getItem(LS_KEY) || localStorage.getItem("vault.attn.dismissed");
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        // Migrate array-of-keys → map with far-future ISO (permanent dismiss)
        if (Array.isArray(parsed)) {
            return Object.fromEntries(parsed.map((k) => [k, FAR_FUTURE]));
        }
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch { return {}; }
}

export default function AttentionTodayStrip() {
    const [d, setD] = useState(null);
    const [snoozes, setSnoozes] = useState(readSnoozes);

    useEffect(() => {
        api.get("/finance-review").then((r) => setD(r.data)).catch(() => setD(null));
    }, []);

    useEffect(() => {
        try { localStorage.setItem(LS_KEY, JSON.stringify(snoozes)); } catch { /* noop */ }
    }, [snoozes]);

    const nowIso = new Date().toISOString();
    // Prune expired snoozes so the state stays small
    useEffect(() => {
        const cleaned = Object.fromEntries(
            Object.entries(snoozes).filter(([, until]) => until > nowIso),
        );
        if (Object.keys(cleaned).length !== Object.keys(snoozes).length) {
            setSnoozes(cleaned);
        }
    }, []);

    const snoozeFor = (key, days) => {
        const until = new Date(Date.now() + days * 86400000).toISOString();
        setSnoozes((prev) => ({ ...prev, [key]: until }));
    };
    const dismissNow = (key) => setSnoozes((prev) => ({ ...prev, [key]: FAR_FUTURE }));
    const restore = (key) => setSnoozes((prev) => {
        const { [key]: _drop, ...rest } = prev;
        return rest;
    });
    const restoreAll = () => setSnoozes({});

    const activeSnoozes = useMemo(
        () => Object.entries(snoozes).filter(([, until]) => until > nowIso),
        [snoozes, nowIso],
    );
    const snoozedKeys = new Set(activeSnoozes.map(([k]) => k));

    if (!d) {
        return <div className="h-40 mb-8 bg-stone-50 rounded-2xl animate-pulse" />;
    }

    const rowFor = (key) => {
        const arr = d[key] || [];
        return { count: arr.length, first: arr[0] || null };
    };
    const TONE_RANK = { rose: 0, amber: 1, stone: 2 };
    const visible = BUCKETS.map((b) => ({ ...b, ...rowFor(b.key) }))
        .filter((b) => b.count > 0 && !snoozedKeys.has(b.key))
        .sort((a, b) => (TONE_RANK[a.tone] - TONE_RANK[b.tone]) || (b.count - a.count));

    if (visible.length === 0) {
        return (
            <section
                className="mb-8 bg-emerald-50 border border-emerald-200 rounded-2xl p-6 flex items-center gap-4"
                data-testid="attention-empty"
            >
                <span className="text-3xl">✓</span>
                <div className="flex-1">
                    <div className="font-display text-2xl text-emerald-900">Nothing needs your attention.</div>
                    <div className="text-sm text-emerald-800 mt-1">
                        Payments up to date, statements uploaded, expenses categorised. Enjoy the calm.
                    </div>
                </div>
                {activeSnoozes.length > 0 && (
                    <button
                        onClick={restoreAll}
                        className="text-[12px] text-emerald-900 underline hover:no-underline"
                        data-testid="attn-restore-all"
                    >
                        Show {activeSnoozes.length} snoozed
                    </button>
                )}
            </section>
        );
    }

    return (
        <section className="mb-8" data-testid="attention-strip">
            <div className="flex items-end justify-between mb-4">
                <div>
                    <div className="eyebrow">Finance Action Center</div>
                    <h2 className="font-display text-2xl text-stone-900 mt-1 dark:text-white">
                        What needs your attention today.
                    </h2>
                </div>
                <Link
                    to="/finance-review"
                    className="text-[12px] font-medium text-stone-700 hover:text-stone-900 inline-flex items-center gap-1"
                    data-testid="attention-see-all"
                >
                    See full Action Center <ArrowRight size={12} />
                </Link>
            </div>

            {activeSnoozes.length > 0 && (
                <div className="mb-3 text-[11px] text-stone-500 flex items-center gap-2 flex-wrap">
                    <span>
                        {activeSnoozes.length} bucket{activeSnoozes.length > 1 ? "s" : ""} snoozed.
                    </span>
                    {activeSnoozes.map(([key, until]) => {
                        const bucketLabel = BUCKETS.find((b) => b.key === key)?.label || key;
                        const daysLeft = Math.max(0, Math.ceil((new Date(until).getTime() - Date.now()) / 86400000));
                        return (
                            <button
                                key={key}
                                onClick={() => restore(key)}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-stone-100 dark:bg-stone-800 hover:bg-stone-200"
                                title={`Snoozed until ${until.slice(0, 10)}`}
                                data-testid={`attn-restore-${key}`}
                            >
                                {bucketLabel}
                                <span className="text-stone-400">
                                    {until === FAR_FUTURE ? "· dismissed" : `· ${daysLeft}d`}
                                </span>
                                <span className="text-stone-800 dark:text-white">×</span>
                            </button>
                        );
                    })}
                    <button
                        className="text-stone-800 dark:text-white underline hover:no-underline"
                        onClick={restoreAll}
                        data-testid="attn-restore-all"
                    >
                        Show all
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {visible.map((b) => {
                    const tone = TONE_MAP[b.tone] || TONE_MAP.stone;
                    const Ic = b.icon;
                    return (
                        <div
                            key={b.key}
                            className={`bg-white border ${tone.border} rounded-xl p-4 flex flex-col dark:bg-stone-900 dark:border-stone-800`}
                            data-testid={`attn-${b.key}`}
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <span className={`w-6 h-6 rounded-md ${tone.bg} ${tone.text} flex items-center justify-center`}>
                                    <Ic size={12} />
                                </span>
                                <div className="text-[11px] uppercase tracking-widest font-semibold text-stone-500">
                                    {b.label}
                                </div>
                                <span className={`ml-auto text-[11px] font-bold tabular-nums ${tone.text}`}>
                                    {b.count}
                                </span>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button
                                            className="ml-1 text-stone-400 hover:text-stone-700 p-0.5 rounded"
                                            title="Snooze options"
                                            data-testid={`attn-menu-${b.key}`}
                                        >
                                            <MoreVertical size={14} />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-44">
                                        <DropdownMenuLabel className="text-[11px]">Snooze this bucket</DropdownMenuLabel>
                                        <DropdownMenuItem
                                            onClick={() => snoozeFor(b.key, 1)}
                                            data-testid={`attn-snooze-1d-${b.key}`}
                                        >
                                            For 1 day
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            onClick={() => snoozeFor(b.key, 7)}
                                            data-testid={`attn-snooze-7d-${b.key}`}
                                        >
                                            For 7 days
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            onClick={() => snoozeFor(b.key, 30)}
                                            data-testid={`attn-snooze-30d-${b.key}`}
                                        >
                                            For 30 days
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            onClick={() => dismissNow(b.key)}
                                            className="text-rose-700 focus:text-rose-800"
                                            data-testid={`attn-dismiss-${b.key}`}
                                        >
                                            Dismiss until reset
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                            {b.first && (
                                <div className="text-[13px] text-stone-800 dark:text-stone-100 truncate">
                                    {b.first.label || b.first.title || b.first.merchant}
                                </div>
                            )}
                            {b.first?.outstanding != null && (
                                <div className="text-[11px] text-stone-500 tabular-nums mt-0.5">
                                    {fmtMoney(b.first.outstanding)}
                                    {b.first.days_left != null && (
                                        <span className="ml-2">
                                            {b.first.days_left < 0 ? `${Math.abs(b.first.days_left)}d overdue`
                                                : b.first.days_left === 0 ? "today"
                                                    : `${b.first.days_left}d left`}
                                        </span>
                                    )}
                                </div>
                            )}
                            <Link
                                to={b.first?.card_id ? `${b.path}?card=${b.first.card_id}` : b.path}
                                className="mt-3 pt-3 border-t border-stone-100 dark:border-stone-800 inline-flex items-center gap-1 text-[12px] font-medium text-stone-900 dark:text-white hover:underline"
                                data-testid={`attn-cta-${b.key}`}
                            >
                                {b.cta} <ArrowRight size={11} />
                            </Link>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
